<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  subjects: { type: Array, required: true },
  grade: { type: Number, required: true },
  selected: { type: String, default: '' },
  path: { type: Array, default: () => [] },
})
const emit = defineEmits(['select'])
const search = ref('')
const expanded = ref(new Set())
const normalize = text => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd')
const query = computed(() => normalize(search.value.trim()))

// Khi tìm kiếm: chỉ giữ chương / bài khớp, và mở sẵn mọi nhánh còn lại.
const visibleSubjects = computed(() => {
  if (!query.value) return props.subjects
  const match = text => normalize(text).includes(query.value)
  return props.subjects.map(subject => ({
    ...subject,
    semesters: subject.semesters.map(sem => ({
      ...sem,
      chapters: sem.chapters.map(chapter => match(chapter.title) ? chapter : { ...chapter, lessons: chapter.lessons.filter(lesson => match(lesson.title)) })
        .filter(chapter => match(chapter.title) || chapter.lessons.length),
    })).filter(sem => sem.chapters.length),
  })).filter(subject => match(subject.title) || subject.semesters.length)
})

function isOpen(key) { return !!query.value || expanded.value.has(key) }
function toggle(key) {
  const next = new Set(expanded.value)
  next.has(key) ? next.delete(key) : next.add(key)
  expanded.value = next
}
function select(key) {
  const next = new Set(expanded.value)
  next.add(key)
  expanded.value = next
  emit('select', key)
}

// Mở sẵn các nhánh cha của mục đang chọn (ví dụ khi vào từ đường dẫn).
watch(() => props.path, path => {
  if (!path.length) return
  expanded.value = new Set([...expanded.value, ...path])
}, { immediate: true })
</script>

<template>
  <div class="content-tree">
    <div class="tree-search"><input v-model="search" class="form-control" type="search" placeholder="Tìm chương hoặc bài trong lớp này…" aria-label="Tìm trong chương trình"></div>
    <button type="button" class="tree-new" :class="{ active: selected === 'new-subject' }" @click="emit('select', 'new-subject')">＋ Tạo môn cho lớp {{ grade }}</button>
    <p v-if="!visibleSubjects.length" class="tree-empty">{{ query ? 'Không tìm thấy nội dung phù hợp.' : `Lớp ${grade} chưa có môn học.` }}</p>
    <ul class="tree-level" role="tree" aria-label="Cây chương trình">
      <li v-for="subject in visibleSubjects" :key="subject.id" role="treeitem" :aria-expanded="isOpen(`subject-${subject.id}`)">
        <div class="tree-node subject-node" :class="{ active: selected === `subject-${subject.id}` }">
          <button type="button" class="tree-caret" :class="{ open: isOpen(`subject-${subject.id}`) }" :aria-label="isOpen(`subject-${subject.id}`) ? 'Thu gọn' : 'Mở rộng'" @click="toggle(`subject-${subject.id}`)">›</button>
          <button type="button" class="tree-label" @click="select(`subject-${subject.id}`)"><span>{{ subject.icon }} {{ subject.title }}</span><em v-if="!subject.is_published">Nháp</em></button>
        </div>
        <ul v-if="isOpen(`subject-${subject.id}`)" class="tree-level" role="group">
          <li v-for="sem in subject.semesters" :key="sem.id" role="treeitem">
            <div class="tree-node" :class="{ active: selected === `semester-${sem.id}` }">
              <button type="button" class="tree-caret" :class="{ open: isOpen(`semester-${sem.id}`) }" :aria-label="isOpen(`semester-${sem.id}`) ? 'Thu gọn' : 'Mở rộng'" @click="toggle(`semester-${sem.id}`)">›</button>
              <button type="button" class="tree-label" @click="select(`semester-${sem.id}`)"><span>{{ sem.title }}</span><small>{{ sem.chapters.length }} chương</small></button>
            </div>
            <ul v-if="isOpen(`semester-${sem.id}`)" class="tree-level" role="group">
              <li v-for="chapter in sem.chapters" :key="chapter.id" role="treeitem">
                <div class="tree-node" :class="{ active: selected === `chapter-${chapter.id}` }">
                  <button type="button" class="tree-caret" :class="{ open: isOpen(`chapter-${chapter.id}`) }" :aria-label="isOpen(`chapter-${chapter.id}`) ? 'Thu gọn' : 'Mở rộng'" @click="toggle(`chapter-${chapter.id}`)">›</button>
                  <button type="button" class="tree-label" @click="select(`chapter-${chapter.id}`)"><span>{{ chapter.title }}</span><small>{{ chapter.lessons.length }} bài</small></button>
                </div>
                <ul v-if="isOpen(`chapter-${chapter.id}`)" class="tree-level" role="group">
                  <li v-for="lesson in chapter.lessons" :key="lesson.id" role="treeitem">
                    <div class="tree-node lesson-node" :class="{ active: selected === `lesson-${lesson.id}` }">
                      <button type="button" class="tree-label" @click="emit('select', `lesson-${lesson.id}`)"><span>{{ lesson.title }}</span><em v-if="!lesson.is_published">Nháp</em><small v-else>{{ lesson.question_count }} câu</small></button>
                    </div>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.content-tree { display: flex; flex-direction: column; gap: 10px; padding: 16px; }
.tree-search .form-control { height: 42px; }
.tree-new { padding: 10px 12px; border: 1px dashed #cfc9f3; border-radius: 11px; background: #faf9ff; color: var(--teal); font-size: 12px; font-weight: 700; text-align: left; cursor: pointer; }
.tree-new.active, .tree-new:hover { background: var(--mint); }
.tree-empty { margin: 8px 4px; color: var(--muted); font-size: 12px; }
.tree-level { margin: 0; padding: 0; list-style: none; }
.tree-level .tree-level { margin-left: 14px; padding-left: 6px; border-left: 1px solid #eceaf6; }
.tree-node { display: flex; align-items: center; gap: 2px; border-radius: 9px; }
.tree-node:hover { background: #f7f6fd; }
.tree-node.active { background: var(--mint); }
.tree-node.active .tree-label span { color: var(--teal-dark); font-weight: 700; }
.lesson-node { padding-left: 24px; }
.tree-caret { flex: 0 0 24px; height: 28px; border: 0; background: none; color: #9a9fb5; font-size: 16px; cursor: pointer; transition: transform .15s; }
.tree-caret.open { transform: rotate(90deg); }
.tree-label { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; padding: 7px 8px 7px 2px; border: 0; background: none; text-align: left; cursor: pointer; }
.tree-label span { flex: 1; min-width: 0; color: var(--ink); font-size: 12px; line-height: 1.45; }
.subject-node .tree-label span { font-size: 13px; font-weight: 700; }
.tree-label small { flex-shrink: 0; color: #a0a5b9; font-size: 10px; }
.tree-label em { flex-shrink: 0; padding: 2px 7px; border-radius: 8px; background: #ece5ff; color: #6448a8; font-size: 10px; font-style: normal; font-weight: 700; }
</style>
